import {currentUserId, db} from "@/plugins/fire-base";
import {addDoc, collection, doc, query, setDoc, where} from "firebase/firestore";
import {executeQuery} from "@/dataLayer/service/utils";
import GlobalConfig from "@/dataLayer/repository/GlobalSettingManager";
import {getCurrentRestaurantInfo} from "@/dataLayer/repository/restaurantInfo";
import {orderBy} from "lodash-es";
import dayjs from "dayjs";

const path = 'takeaway-user-order'
const paypalPath = 'takeaway-checkout-paypal'
const errorPath = 'takeaway-error-order'

function toJsonText(value, maxLength = 6000) {
    try {
        if (value === undefined || value === null) {
            return ''
        }
        if (typeof value === 'string') {
            return value.length > maxLength ? value.slice(0, maxLength) + '...' : value
        }
        const seen = new WeakSet()
        const str = JSON.stringify(value, (key, val) => {
            if (typeof val === 'function') {
                return '[Function]'
            }
            if (val instanceof Error) {
                return {name: val.name, message: val.message, stack: val.stack}
            }
            if (typeof val === 'object' && val !== null) {
                if (seen.has(val)) {
                    return '[Circular]'
                }
                seen.add(val)
            }
            return val
        })
        if (!str) {
            return ''
        }
        return str.length > maxLength ? str.slice(0, maxLength) + '...' : str
    } catch (e) {
        try {
            return String(value)
        } catch (e2) {
            return '[Unserializable]'
        }
    }
}

/**
 * Convert any value into something firestore accepts.
 * undefined / functions are dropped, Error and class instances (axios response) become JSON text.
 */
function safeValue(value, depth = 0) {
    if (value === null || value === undefined) {
        return value
    }
    const type = typeof value
    if (type === 'string' || type === 'number' || type === 'boolean') {
        return value
    }
    if (type === 'function' || type === 'symbol' || type === 'bigint') {
        return undefined
    }
    if (value instanceof Date) {
        return value
    }
    if (depth > 6) {
        return toJsonText(value)
    }
    if (value instanceof Error) {
        return errorToLog(value)
    }
    if (Array.isArray(value)) {
        return value.map(item => {
            const safe = safeValue(item, depth + 1)
            return safe === undefined ? null : safe
        })
    }
    if (type === 'object') {
        const proto = Object.getPrototypeOf(value)
        if (proto !== Object.prototype && proto !== null) {
            return toJsonText(value)
        }
        const result = {}
        Object.keys(value).forEach(key => {
            const safe = safeValue(value[key], depth + 1)
            if (safe !== undefined) {
                result[key] = safe
            }
        })
        return result
    }
    return undefined
}

/**
 * Turn an Error / axios response / anything into a plain, firestore friendly log object.
 */
export function errorToLog(error) {
    if (error === null || error === undefined) {
        return {message: 'Unknown error', time: dayjs().format('YYYY-MM-DD HH:mm:ss')}
    }
    if (typeof error !== 'object') {
        return {message: String(error), time: dayjs().format('YYYY-MM-DD HH:mm:ss')}
    }
    const detail = toJsonText({
        message: error.message,
        name: error.name,
        stack: error.stack,
        status: error.status ?? error.response?.status,
        requestUrl: error.config?.url,
        requestData: error.config?.data,
        responseData: error.data ?? error.response?.data ?? error.response
    })
    return {
        message: error.message
            ?? error.data?.message
            ?? error.data?.info
            ?? error.response?.data?.message
            ?? error.statusText
            ?? detail
            ?? String(error),
        name: error.name,
        status: error.status ?? error.response?.status,
        stack: error.stack,
        detail,
        time: dayjs().format('YYYY-MM-DD HH:mm:ss')
    }
}

export async function setOrderForUser(orderInfo) {
    orderInfo.userId = currentUserId()
    orderInfo.dataTerminalId = GlobalConfig.dataTerminalId
    orderInfo.deviceId = GlobalConfig.deviceId
    orderInfo.restaurantName = (await getCurrentRestaurantInfo()).name
    const id = orderInfo.id.toString()
    try {
        await setDoc(doc(db, path, id), orderInfo)
        return true
    } catch (e) {
        console.log(e)
        return false
    }

}

export async function setPayPalOrderForUser(orderInfo) {
    orderInfo.userId = currentUserId()
    orderInfo.dataTerminalId = GlobalConfig.dataTerminalId
    orderInfo.deviceId = GlobalConfig.deviceId
    orderInfo.restaurantName = (await getCurrentRestaurantInfo()).name
    orderInfo.createDate = dayjs().format('YYYY-MM-DD HH:mm:ss')
    try {
        return (await addDoc(collection(db, paypalPath), safeValue(orderInfo))).id
    } catch (e) {
        console.log(e)
        return false
    }

}

export async function setErrorOrderForUser(orderInfo) {
    orderInfo.userId = currentUserId()
    orderInfo.dataTerminalId = GlobalConfig.dataTerminalId
    orderInfo.deviceId = GlobalConfig.deviceId
    orderInfo.restaurantName = (await getCurrentRestaurantInfo()).name
    orderInfo.createDate = dayjs().format('YYYY-MM-DD HH:mm:ss')
    try {
        return (await addDoc(collection(db, errorPath), safeValue(orderInfo))).id
    } catch (e) {
        console.log(e)
        return false
    }

}

export async function updatePayPalOrderForUser(orderInfo,firebaseId) {
    try {
        if (!firebaseId) {
            return false
        }
        // setDoc + merge: also works when the document does not exist yet,
        // and safeValue removes/convert values firestore cannot store (Error, axios response...)
        await setDoc(doc(db, paypalPath, firebaseId), safeValue(orderInfo), {merge: true})
        return true
    } catch (e) {
        console.log(e)
        return false
    }

}


export async function getAllOrdersForUser() {
    return orderBy(await executeQuery(query(collection(db, path),
        where('userId', '==', currentUserId()))),
        (order) => [order.addressInfo.date + order?.addressInfo.time],
        ['desc'])
}
